import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Alert,
  InputAdornment,
  IconButton,
  useTheme,
  useMediaQuery,
  alpha,
  GlobalStyles,
  Stack,
} from "@mui/material";
import {
  Visibility,
  VisibilityOff,
  ArrowBack,
  ArrowForward,
} from "@mui/icons-material";
import { useFormik } from "formik";
import * as yup from "yup";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { LoginFormData } from "../types";

/* =====================================================
   Design tokens — mirrored from LandingPage
   ===================================================== */
const C = {
  ink: "#0F1419",
  inkDeep: "#080B0E",
  inkSoft: "#1A2128",
  parchment: "#F5F1E8",
  parchmentSoft: "#EDE7D7",
  hairline: "#DDD6C2",
  emerald: "#3B7A57",
  emeraldDeep: "#2A5A3F",
  emeraldGlow: "#7BAE5A",
  cerulean: "#2B8FB8",
  ceruleanLight: "#5DB3D4",
  text: "#1A1F26",
  textMuted: "#5A6470",
  textOnDark: "#F5F1E8",
  textOnDarkMuted: "rgba(245, 241, 232, 0.65)",
  fieldBg: "#FFFFFF",
  fieldBorder: "#E2DDC9",
  fieldBorderHover: "#C4BD9F",
  error: "#B94A48",
};

const FONT_DISPLAY = `'Fraunces', 'Playfair Display', Georgia, serif`;
const FONT_BODY = `'Inter', system-ui, -apple-system, sans-serif`;
const FONT_MONO = `'JetBrains Mono', 'Courier New', monospace`;

/* ---------- Validation (unchanged from original) ---------- */
const isValidIdentifier = (value: string) => {
  if (!value) return false;
  if (value.includes("@")) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
  }
  return /^[A-Za-z0-9]{2,5}-\d{6}$/.test(value);
};

const validationSchema = yup.object({
  identifier: yup
    .string()
    .required("Email or User ID is required")
    .test(
      "valid-identifier",
      "Enter a valid email or User ID (e.g., APS-000001)",
      isValidIdentifier
    ),
  password: yup.string().required("Password is required"),
});

/* ---------- Logo (uses the same /images/edubook-logo.png) ---------- */
const LogoMark: React.FC<{ onDark?: boolean; size?: number }> = ({
  onDark,
  size = 64,
}) => (
  <Box
    component="img"
    src="/images/edubook-logo.png"
    alt="Edubook"
    sx={{
      height: size,
      width: "auto",
      objectFit: "contain",
      filter: onDark
        ? "drop-shadow(0 4px 14px rgba(0,0,0,0.5))"
        : "drop-shadow(0 2px 8px rgba(15,20,25,0.12))",
      userSelect: "none",
    }}
  />
);

/* =====================================================
   Login Page
   ===================================================== */
const LoginPage: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  // Inject Google Fonts (same set as landing page)
  useEffect(() => {
    const HREF =
      "https://fonts.googleapis.com/css2?family=Fraunces:ital,opsz,wght@0,9..144,400;0,9..144,500;0,9..144,600;0,9..144,700;1,9..144,400;1,9..144,500;1,9..144,600&family=Inter:wght@300;400;500;600;700&family=JetBrains+Mono:wght@400;500;600&display=swap";
    if (document.querySelector(`link[href="${HREF}"]`)) return;

    const pre1 = document.createElement("link");
    pre1.rel = "preconnect";
    pre1.href = "https://fonts.googleapis.com";
    const pre2 = document.createElement("link");
    pre2.rel = "preconnect";
    pre2.href = "https://fonts.gstatic.com";
    pre2.crossOrigin = "anonymous";
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = HREF;

    document.head.appendChild(pre1);
    document.head.appendChild(pre2);
    document.head.appendChild(link);
  }, []);

  const formik = useFormik<LoginFormData>({
    initialValues: { identifier: "", password: "" },
    validationSchema,
    onSubmit: async (values) => {
      setLoading(true);
      setError(null);
      try {
        await login(values.identifier, values.password);
        // Authenticated routes are mounted under /app. `/app` itself indexes
        // to /app/dashboard, so users without dashboard permission still see
        // the layout (sidebar + access-denied placeholder) and can navigate
        // from there to a module they're allowed in.
        navigate("/app");
      } catch (err: any) {
        setError(err.message || "Login failed");
      } finally {
        setLoading(false);
      }
    },
  });

  /* ---------- Shared field styles ---------- */
  const fieldSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "12px",
      bgcolor: C.fieldBg,
      fontSize: "0.95rem",
      fontFamily: FONT_BODY,
      transition: "all 0.25s ease",
      "& fieldset": {
        borderColor: C.fieldBorder,
        borderWidth: "1.5px",
        transition: "all 0.25s ease",
      },
      "&:hover fieldset": {
        borderColor: C.fieldBorderHover,
      },
      "&.Mui-focused": {
        bgcolor: "#fff",
        boxShadow: `0 0 0 4px ${alpha(C.emerald, 0.08)}`,
        "& fieldset": {
          borderColor: C.emerald,
          borderWidth: "1.5px",
        },
      },
      "&.Mui-error fieldset": {
        borderColor: C.error,
      },
      "&.Mui-error.Mui-focused": {
        boxShadow: `0 0 0 4px ${alpha(C.error, 0.08)}`,
      },
    },
    "& .MuiInputBase-input": {
      py: 1.65,
      px: 1.75,
      color: C.text,
      "&::placeholder": {
        color: alpha(C.textMuted, 0.7),
        opacity: 1,
      },
    },
    "& .MuiFormHelperText-root": {
      ml: 0.5,
      mt: 0.75,
      fontFamily: FONT_BODY,
      fontSize: "0.78rem",
      color: C.error,
    },
  };

  return (
    <>
      <GlobalStyles
        styles={{
          "html, body": {
            fontFamily: FONT_BODY,
            background: C.parchment,
            color: C.text,
            margin: 0,
            overflowX: "hidden",
          },
          "@keyframes fadeUp": {
            from: { opacity: 0, transform: "translateY(20px)" },
            to: { opacity: 1, transform: "translateY(0)" },
          },
          "@keyframes loginGlow": {
            "0%, 100%": { opacity: 0.55 },
            "50%": { opacity: 0.85 },
          },
          "*::selection": {
            background: C.emerald,
            color: C.parchment,
          },
        }}
      />

      <Box
        sx={{
          minHeight: "100vh",
          width: "100%",
          maxWidth: "100vw",
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          bgcolor: C.parchment,
          overflowX: "hidden",
        }}
      >
        {/* ============================================================
            MOBILE HEADER (only shown on mobile)
            ============================================================ */}
        {isMobile && (
          <Box
            sx={{
              position: "sticky",
              top: 0,
              zIndex: 10,
              bgcolor: alpha(C.ink, 0.92),
              backdropFilter: "blur(20px) saturate(180%)",
              borderBottom: `1px solid ${alpha(C.emeraldGlow, 0.12)}`,
              py: 1.5,
              px: 2.5,
            }}
          >
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
            >
              <Box
                onClick={() => navigate("/")}
                sx={{ cursor: "pointer", display: "flex", alignItems: "center" }}
              >
                <LogoMark onDark size={48} />
              </Box>
              <Button
                onClick={() => navigate("/")}
                disableRipple
                startIcon={<ArrowBack sx={{ fontSize: 16 }} />}
                sx={{
                  color: alpha(C.parchment, 0.85),
                  fontFamily: FONT_BODY,
                  fontWeight: 500,
                  fontSize: "0.85rem",
                  textTransform: "none",
                  px: 2,
                  py: 0.75,
                  borderRadius: "999px",
                  border: `1px solid ${alpha(C.parchment, 0.15)}`,
                  "&:hover": {
                    background: alpha(C.emeraldGlow, 0.1),
                    borderColor: alpha(C.emeraldGlow, 0.4),
                    color: C.emeraldGlow,
                  },
                }}
              >
                Home
              </Button>
            </Stack>
          </Box>
        )}

        {/* ============================================================
            LEFT — DARK BRAND PANEL
            ============================================================ */}
        <Box
          sx={{
            flex: 1,
            display: { xs: "none", md: "flex" },
            alignItems: "center",
            justifyContent: "center",
            position: "relative",
            overflow: "hidden",
            background: `linear-gradient(160deg, ${C.ink} 0%, ${C.inkSoft} 60%, ${C.ink} 100%)`,
            color: C.parchment,
            minHeight: "100vh",
            p: 6,
          }}
        >
          {/* Subtle grid texture */}
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              backgroundImage: `radial-gradient(${alpha(
                C.parchment,
                0.045
              )} 1px, transparent 1px)`,
              backgroundSize: "32px 32px",
              maskImage:
                "radial-gradient(ellipse at center, black 30%, transparent 78%)",
              WebkitMaskImage:
                "radial-gradient(ellipse at center, black 30%, transparent 78%)",
              pointerEvents: "none",
            }}
          />

          {/* Ambient glows */}
          <Box
            sx={{
              position: "absolute",
              top: "-20%",
              left: "-20%",
              width: 600,
              height: 600,
              background: `radial-gradient(circle, ${alpha(
                C.emerald,
                0.32
              )} 0%, transparent 70%)`,
              filter: "blur(70px)",
              animation: "loginGlow 8s ease-in-out infinite",
              pointerEvents: "none",
            }}
          />
          <Box
            sx={{
              position: "absolute",
              bottom: "-25%",
              right: "-15%",
              width: 550,
              height: 550,
              background: `radial-gradient(circle, ${alpha(
                C.cerulean,
                0.22
              )} 0%, transparent 70%)`,
              filter: "blur(80px)",
              animation: "loginGlow 10s ease-in-out infinite 2s",
              pointerEvents: "none",
            }}
          />

          {/* ----- Top-left: Back to home link ----- */}
          <Box
            sx={{
              position: "absolute",
              top: 36,
              left: 48,
              zIndex: 2,
            }}
          >
            <Button
              onClick={() => navigate("/")}
              disableRipple
              startIcon={<ArrowBack sx={{ fontSize: 16 }} />}
              sx={{
                color: alpha(C.parchment, 0.7),
                fontFamily: FONT_BODY,
                fontWeight: 500,
                fontSize: "0.88rem",
                textTransform: "none",
                px: 1,
                "&:hover": {
                  background: "transparent",
                  color: C.emeraldGlow,
                },
              }}
            >
              Back to home
            </Button>
          </Box>

          {/* ----- Centered brand block ----- */}
          <Box
            sx={{
              position: "relative",
              zIndex: 1,
              maxWidth: 540,
              animation: "fadeUp 1s cubic-bezier(0.22, 1, 0.36, 1) both",
            }}
          >
            {/* Logo — large, prominent */}
            <Box sx={{ mb: 6 }}>
              <LogoMark onDark size={160} />
            </Box>

            {/* Tagline — large editorial serif */}
            <Typography
              component="h1"
              sx={{
                fontFamily: FONT_DISPLAY,
                fontSize: { md: "2.6rem", lg: "3.2rem" },
                fontWeight: 500,
                color: C.parchment,
                lineHeight: 1.05,
                letterSpacing: "-0.02em",
                mb: 3,
              }}
            >
              The quiet
              <Box
                component="span"
                sx={{
                  fontStyle: "italic",
                  color: C.emeraldGlow,
                  fontWeight: 400,
                }}
              >
                {" "}
                architecture{" "}
              </Box>
              behind your school.
            </Typography>

            {/* One elegant line */}
            <Typography
              sx={{
                fontFamily: FONT_BODY,
                fontSize: { md: "1.05rem", lg: "1.1rem" },
                color: C.textOnDarkMuted,
                lineHeight: 1.7,
                maxWidth: 460,
              }}
            >
              Sign in to continue running every workflow your institution
              depends on — quietly, beautifully, all in one place.
            </Typography>
          </Box>

          {/* Bottom corner attribution */}
          <Typography
            sx={{
              position: "absolute",
              bottom: 36,
              left: 48,
              fontFamily: FONT_DISPLAY,
              fontStyle: "italic",
              fontSize: "0.85rem",
              color: alpha(C.emeraldGlow, 0.7),
              zIndex: 1,
            }}
          >
            Crafted for institutions that care.
          </Typography>
        </Box>

        {/* ============================================================
            RIGHT — FORM PANEL (parchment)
            ============================================================ */}
        <Box
          sx={{
            flex: 1,
            display: "flex",
            alignItems: { xs: "flex-start", md: "center" },
            justifyContent: "center",
            position: "relative",
            overflow: "hidden",
            background: `linear-gradient(180deg, ${C.parchment} 0%, ${C.parchmentSoft} 100%)`,
            minHeight: { xs: "auto", md: "100vh" },
            p: { xs: 0, sm: 4, md: 6 },
          }}
        >
          {/* Soft accent in form panel */}
          <Box
            sx={{
              position: "absolute",
              top: "10%",
              right: "-10%",
              width: 320,
              height: 320,
              borderRadius: "50%",
              background: `radial-gradient(circle, ${alpha(
                C.emerald,
                0.08
              )} 0%, transparent 70%)`,
              filter: "blur(40px)",
              pointerEvents: "none",
              display: { xs: "none", md: "block" },
            }}
          />

          <Box
            sx={{
              width: "100%",
              maxWidth: 480,
              p: { xs: 3, sm: 4, md: 0 },
              mt: { xs: 4, md: 0 },
              mb: { xs: 4, md: 0 },
              animation: "fadeUp 1s 0.15s cubic-bezier(0.22, 1, 0.36, 1) both",
            }}
          >
            {/* Heading */}
            <Box mb={4}>
              <Typography
                sx={{
                  fontFamily: FONT_MONO,
                  fontSize: "0.7rem",
                  letterSpacing: "0.22em",
                  textTransform: "uppercase",
                  color: C.emerald,
                  mb: 2,
                }}
              >
                Sign in
              </Typography>
              <Typography
                component="h1"
                sx={{
                  fontFamily: FONT_DISPLAY,
                  fontSize: { xs: "2rem", sm: "2.4rem", md: "2.6rem" },
                  fontWeight: 500,
                  color: C.text,
                  lineHeight: 1.05,
                  letterSpacing: "-0.02em",
                  mb: 1.5,
                }}
              >
                Welcome
                <Box
                  component="span"
                  sx={{
                    fontStyle: "italic",
                    color: C.emerald,
                    fontWeight: 400,
                  }}
                >
                  {" "}
                  back.
                </Box>
              </Typography>
              <Typography
                sx={{
                  fontFamily: FONT_BODY,
                  fontSize: { xs: "0.95rem", md: "1rem" },
                  color: C.textMuted,
                  lineHeight: 1.6,
                }}
              >
                Sign in to unlock your school's potential.
              </Typography>
            </Box>

            {/* Error alert */}
            {error && (
              <Alert
                severity="error"
                sx={{
                  mb: 3,
                  borderRadius: "12px",
                  border: `1px solid ${alpha(C.error, 0.2)}`,
                  bgcolor: alpha(C.error, 0.06),
                  color: C.error,
                  fontFamily: FONT_BODY,
                  fontSize: "0.9rem",
                  "& .MuiAlert-icon": { color: C.error },
                }}
              >
                {error}
              </Alert>
            )}

            <form onSubmit={formik.handleSubmit} noValidate>
              {/* Identifier */}
              <Box sx={{ mb: 2.5 }}>
                <Typography
                  sx={{
                    fontFamily: FONT_BODY,
                    fontWeight: 500,
                    color: C.text,
                    mb: 1,
                    fontSize: "0.88rem",
                  }}
                >
                  Email or User ID
                </Typography>
                <TextField
                  fullWidth
                  id="identifier"
                  name="identifier"
                  placeholder="user@example.com  or  APS-000001"
                  type="text"
                  value={formik.values.identifier}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.identifier &&
                    Boolean(formik.errors.identifier)
                  }
                  helperText={
                    formik.touched.identifier && formik.errors.identifier
                  }
                  disabled={loading}
                  sx={fieldSx}
                />
              </Box>

              {/* Password */}
              <Box sx={{ mb: 1.5 }}>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  sx={{ mb: 1 }}
                >
                  <Typography
                    sx={{
                      fontFamily: FONT_BODY,
                      fontWeight: 500,
                      color: C.text,
                      fontSize: "0.88rem",
                    }}
                  >
                    Password
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: FONT_BODY,
                      color: C.emerald,
                      cursor: "pointer",
                      fontWeight: 500,
                      fontSize: "0.82rem",
                      transition: "color 0.2s",
                      "&:hover": { color: C.emeraldDeep },
                    }}
                  >
                    Forgot password?
                  </Typography>
                </Stack>
                <TextField
                  fullWidth
                  id="password"
                  name="password"
                  placeholder="• • • • • • • •"
                  type={showPassword ? "text" : "password"}
                  value={formik.values.password}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.password && Boolean(formik.errors.password)
                  }
                  helperText={formik.touched.password && formik.errors.password}
                  disabled={loading}
                  sx={fieldSx}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword((p) => !p)}
                          edge="end"
                          disabled={loading}
                          sx={{
                            color: C.textMuted,
                            mr: 0.5,
                            "&:hover": {
                              color: C.emerald,
                              background: alpha(C.emerald, 0.06),
                            },
                          }}
                        >
                          {showPassword ? (
                            <VisibilityOff sx={{ fontSize: 19 }} />
                          ) : (
                            <Visibility sx={{ fontSize: 19 }} />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>

              {/* Submit */}
              <Button
                type="submit"
                fullWidth
                disabled={loading}
                disableRipple
                sx={{
                  mt: 3.5,
                  py: 1.75,
                  borderRadius: "12px",
                  fontFamily: FONT_BODY,
                  fontSize: "1rem",
                  fontWeight: 600,
                  textTransform: "none",
                  bgcolor: C.ink,
                  color: C.parchment,
                  letterSpacing: "0.005em",
                  position: "relative",
                  overflow: "hidden",
                  transition: "all 0.35s cubic-bezier(0.22, 1, 0.36, 1)",
                  "& .arrow": {
                    ml: 1,
                    fontSize: 18,
                    transition: "transform 0.35s ease",
                  },
                  "&:hover": {
                    bgcolor: C.inkDeep,
                    transform: "translateY(-2px)",
                    boxShadow: `0 14px 30px -8px ${alpha(C.ink, 0.5)}`,
                    "& .arrow": { transform: "translateX(4px)" },
                  },
                  "&:active": { transform: "translateY(0)" },
                  "&.Mui-disabled": {
                    bgcolor: alpha(C.ink, 0.4),
                    color: alpha(C.parchment, 0.6),
                  },
                }}
              >
                {loading ? (
                  <Stack direction="row" alignItems="center" spacing={1.5}>
                    <CircularProgress
                      size={18}
                      sx={{ color: C.emeraldGlow }}
                    />
                    <span>Signing in…</span>
                  </Stack>
                ) : (
                  <>
                    Sign in
                    <ArrowForward className="arrow" />
                  </>
                )}
              </Button>
            </form>

            {/* Hairline divider */}
            {/* <Box
              sx={{
                mt: 4,
                mb: 3,
                height: "1px",
                background: `linear-gradient(to right, transparent, ${C.hairline}, transparent)`,
              }}
            /> */}

            {/* Dev credentials — refined */}
            {/* <Box
              sx={{
                p: 2,
                borderRadius: "10px",
                border: `1px dashed ${alpha(C.emerald, 0.3)}`,
                background: alpha(C.emerald, 0.04),
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                transition: "all 0.25s ease",
                "&:hover": {
                  borderColor: alpha(C.emerald, 0.5),
                  background: alpha(C.emerald, 0.07),
                },
              }}
            >
              <Box
                sx={{
                  px: 0.75,
                  py: 0.25,
                  borderRadius: "6px",
                  background: C.emerald,
                  color: C.parchment,
                  fontFamily: FONT_MONO,
                  fontSize: "0.6rem",
                  fontWeight: 600,
                  letterSpacing: "0.1em",
                  textTransform: "uppercase",
                  flexShrink: 0,
                }}
              >
                Dev
              </Box>
              <Typography
                sx={{
                  fontFamily: FONT_MONO,
                  fontSize: "0.72rem",
                  color: C.textMuted,
                  lineHeight: 1.5,
                  flex: 1,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                abid.neduet@gmail.com / 12345678
              </Typography>
            </Box> */}

            {/* Copyright */}
            <Typography
              sx={{
                mt: 4,
                textAlign: "center",
                fontFamily: FONT_BODY,
                fontSize: "0.78rem",
                color: alpha(C.text, 0.5),
              }}
            >
              © {new Date().getFullYear()} Edubook. All rights reserved.
            </Typography>
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default LoginPage;